import type { ChartAudio, ChartPalette } from '../../chart.js';
import type { Difficulty, Variant } from '../../gameTypes.js';
import type { GuitarChart } from '../../guitarChart.js';
import type { PianoChart } from '../../pianoChart.js';
import { type ManiaLane, type ManiaNote } from '../../maniaNote.js';

/**
 * The two modes that run on the shared 4-lane mania engine. The builder
 * is parameterised over this so the Guitar UGC creator (and, later, a
 * Piano one) can share one assembly path. Only the `mode` discriminator
 * differs between the two chart shapes — both extend `BaseChart` with
 * `notes` / `approachMs` / optional timing windows.
 */
export type ManiaBuilderMode = 'guitar' | 'piano';

/** Resolve the concrete chart type for a given mania mode. */
export type ManiaChartForMode<M extends ManiaBuilderMode> = M extends 'guitar'
  ? GuitarChart
  : PianoChart;

export interface ManiaChartBuilderInit<M extends ManiaBuilderMode> {
  mode: M;
  id: string;
  slug: string;
  title: string;
  description: string;
  bpm: number;
  difficulty: Difficulty;
  /**
   * Density variant. User-built maps are always `'normal'` (the editor
   * authors a single density); the harder tiers are reserved for the
   * tool-imported builtin catalogue.
   */
  variant: Variant;
  totalBeats: number;
  palette: ChartPalette;
  /**
   * Audio bundle the chart is timed against. For UGC this is copied
   * verbatim from the chosen builtin song so `audio.bpm === bpm`,
   * `offsetMs`, and `durationSec` all stay self-consistent (the chart
   * validator enforces `audio.bpm === chart.bpm`).
   */
  audio: ChartAudio;
  approachMs: number;
  perfectWindowMs?: number;
  goodWindowMs?: number;
  mapper?: string;
}

/**
 * Assembles a validated-shape mania chart ({@link GuitarChart} or
 * {@link PianoChart}) from a song/audio template plus a set of
 * {@link ManiaNote}s. Mirrors Pulse's `ChartBuilder` so the UGC editor
 * follows a parallel pattern across modes.
 *
 * The builder only assembles — it does NOT validate. Callers run the
 * mode's `validateChart` against `build()`'s output and surface issues
 * to the author (the editor) or assert on them (tests).
 */
export class ManiaChartBuilder<M extends ManiaBuilderMode> {
  private readonly init: ManiaChartBuilderInit<M>;
  private notes: ManiaNote[] = [];
  private noteCounter = 0;

  constructor(init: ManiaChartBuilderInit<M>) {
    this.init = init;
  }

  /**
   * Add a single note. `holdEndBeat` makes it a hold; `dualPartnerLane`
   * lights a second lane on the same beat (a chord accent). An explicit
   * `id` is honoured (so the editor's stable per-note ids round-trip);
   * otherwise a sequential id is generated.
   */
  note(
    beat: number,
    lane: ManiaLane,
    opts: { holdEndBeat?: number; dualPartnerLane?: ManiaLane; id?: string } = {},
  ): this {
    const id = opts.id ?? `n-${(this.noteCounter++).toString().padStart(6, '0')}`;
    const note: ManiaNote = { id, beat, lane };
    if (opts.holdEndBeat !== undefined) {
      note.holdEndBeat = opts.holdEndBeat;
    }
    if (opts.dualPartnerLane !== undefined) {
      note.dualPartnerLane = opts.dualPartnerLane;
    }
    this.notes.push(note);
    return this;
  }

  /** Bulk-add pre-built notes (e.g. the editor's working set). Copies each. */
  addNotes(notes: ReadonlyArray<ManiaNote>): this {
    for (const n of notes) {
      const copy: ManiaNote = { id: n.id, beat: n.beat, lane: n.lane };
      if (n.holdEndBeat !== undefined) {
        copy.holdEndBeat = n.holdEndBeat;
      }
      if (n.dualPartnerLane !== undefined) {
        copy.dualPartnerLane = n.dualPartnerLane;
      }
      this.notes.push(copy);
    }
    return this;
  }

  build(): ManiaChartForMode<M> {
    const sortedNotes = [...this.notes].sort(
      (a, b) => a.beat - b.beat || a.id.localeCompare(b.id),
    );
    // Guitar and Piano charts are structurally identical apart from the
    // `mode` discriminator, so we build one common envelope and let the
    // mode-parameterised return type narrow it for the caller.
    const chart = {
      mode: this.init.mode,
      id: this.init.id,
      slug: this.init.slug,
      title: this.init.title,
      description: this.init.description,
      bpm: this.init.bpm,
      difficulty: this.init.difficulty,
      variant: this.init.variant,
      totalBeats: this.init.totalBeats,
      palette: this.init.palette,
      audio: this.init.audio,
      notes: sortedNotes,
      approachMs: this.init.approachMs,
    } as ManiaChartForMode<M>;
    if (this.init.perfectWindowMs !== undefined) {
      chart.perfectWindowMs = this.init.perfectWindowMs;
    }
    if (this.init.goodWindowMs !== undefined) {
      chart.goodWindowMs = this.init.goodWindowMs;
    }
    if (this.init.mapper !== undefined) {
      chart.mapper = this.init.mapper;
    }
    return chart;
  }
}
